import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  applyHeadBrandingAssets,
  applyInstitutionalBrandingAssets,
} from "../../services/local/institutionalBranding";
import { setDocumentTitle } from "../../services/local/pageTitle";
import { supabase } from "../../services/supabase/client";
import { getPublicDisplayBoardByPairingCode } from "../../services/supabase/qrService";

const DISPLAY_FALLBACK_REFRESH_INTERVAL_MS = 8000;
const DISPLAY_PAIRING_STORAGE_KEY = "mozzoapp:public-display:pairing-code";

const readStoredDisplayCode = () => {
  if (typeof window === "undefined") {
    return "";
  }

  return String(window.localStorage.getItem(DISPLAY_PAIRING_STORAGE_KEY) || "").trim();
};

const normalizePairingCodeInput = (value) =>
  String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9-]/g, "");

const formatPreparationNumber = (value) =>
  `#${String(Math.max(0, Number(value || 0))).padStart(3, "0")}`;

const formatBoardTimestamp = (value) => {
  if (!value) {
    return "Sin sincronizacion todavia";
  }

  try {
    return new Date(value).toLocaleTimeString("es-AR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (error) {
    return "Sin sincronizacion todavia";
  }
};

function DisplayPedidos() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCode = useMemo(() => {
    const queryCode = normalizePairingCodeInput(searchParams.get("code"));

    if (queryCode) {
      return queryCode;
    }

    return normalizePairingCodeInput(readStoredDisplayCode());
  }, [searchParams]);
  const [codeInput, setCodeInput] = useState(initialCode);
  const [linkedCode, setLinkedCode] = useState(initialCode);
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(Boolean(initialCode));
  const [errorMessage, setErrorMessage] = useState("");
  const boardRequestRef = useRef({ code: "", promise: null, requestId: 0 });

  const branch = board?.branch || null;
  const preparingOrders = Array.isArray(board?.preparingOrders)
    ? board.preparingOrders
    : [];
  const readyOrders = Array.isArray(board?.readyOrders) ? board.readyOrders : [];
  const branchIconUrl =
    branch?.iconLogoUrl || branch?.loadingLogoUrl || branch?.logoCompactUrl || "";

  const persistLinkedCode = useCallback(
    (nextCode) => {
      const safeCode = normalizePairingCodeInput(nextCode);

      if (typeof window !== "undefined") {
        if (safeCode) {
          window.localStorage.setItem(DISPLAY_PAIRING_STORAGE_KEY, safeCode);
        } else {
          window.localStorage.removeItem(DISPLAY_PAIRING_STORAGE_KEY);
        }
      }

      setSearchParams(safeCode ? { code: safeCode } : {}, { replace: true });
      setLinkedCode(safeCode);
      setCodeInput(safeCode);
    },
    [setSearchParams]
  );

  const loadBoard = useCallback(async (pairingCode, { quiet = false } = {}) => {
    const safeCode = normalizePairingCodeInput(pairingCode);

    if (!safeCode) {
      setBoard(null);
      setLoading(false);
      return;
    }

    if (
      boardRequestRef.current.promise &&
      boardRequestRef.current.code === safeCode
    ) {
      return boardRequestRef.current.promise;
    }

    const requestId = boardRequestRef.current.requestId + 1;

    if (!quiet) {
      setLoading(true);
    }

    const requestPromise = (async () => {
      try {
        const nextBoard = await getPublicDisplayBoardByPairingCode(safeCode);

        if (boardRequestRef.current.requestId === requestId) {
          setBoard(nextBoard);
          setErrorMessage("");
        }
      } catch (error) {
        if (boardRequestRef.current.requestId === requestId) {
          if (!quiet) {
            setBoard(null);
          }

          setErrorMessage(
            error?.message ||
              "No pudimos abrir la pantalla publica con ese codigo."
          );
        }
      } finally {
        if (boardRequestRef.current.requestId === requestId && !quiet) {
          setLoading(false);
        }
      }
    })();

    boardRequestRef.current = {
      code: safeCode,
      promise: requestPromise,
      requestId,
    };

    try {
      return await requestPromise;
    } finally {
      if (boardRequestRef.current.promise === requestPromise) {
        boardRequestRef.current = {
          ...boardRequestRef.current,
          promise: null,
        };
      }
    }
  }, []);

  useEffect(() => {
    const queryCode = normalizePairingCodeInput(searchParams.get("code"));

    if (!queryCode) {
      return;
    }

    if (typeof window !== "undefined") {
      window.localStorage.setItem(DISPLAY_PAIRING_STORAGE_KEY, queryCode);
    }

    setLinkedCode((currentValue) => (currentValue === queryCode ? currentValue : queryCode));
    setCodeInput((currentValue) => (currentValue === queryCode ? currentValue : queryCode));
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;

    if (!linkedCode) {
      setBoard(null);
      setLoading(false);
      return undefined;
    }

    const runLoad = async (quiet = false) => {
      try {
        if (!cancelled) {
          await loadBoard(linkedCode, { quiet });
        }
      } catch (error) {
        // El manejo ya vive adentro de loadBoard.
      }
    };

    runLoad(false);

    const intervalId = window.setInterval(() => {
      runLoad(true);
    }, DISPLAY_FALLBACK_REFRESH_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [linkedCode, loadBoard]);

  useEffect(() => {
    const branchId = String(board?.branch?.id || "").trim();

    if (!branchId || !linkedCode || !supabase) {
      return undefined;
    }

    const channel = supabase
      .channel(`public-display:${branchId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "branch_display_state",
          filter: `branch_id=eq.${branchId}`,
        },
        () => {
          loadBoard(linkedCode, { quiet: true });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [board?.branch?.id, linkedCode, loadBoard]);

  useEffect(() => {
    if (branch?.name) {
      setDocumentTitle("Display", branch.name);
    } else {
      setDocumentTitle("Display");
    }

    if (branchIconUrl) {
      applyHeadBrandingAssets(branchIconUrl);
      return;
    }

    applyInstitutionalBrandingAssets();
  }, [branch?.name, branchIconUrl]);

  const handleSubmitCode = async (event) => {
    event.preventDefault();
    const safeCode = normalizePairingCodeInput(codeInput);

    if (!safeCode) {
      setErrorMessage("Escribe el codigo de la sucursal para vincular esta pantalla.");
      return;
    }

    persistLinkedCode(safeCode);
    await loadBoard(safeCode, { quiet: false });
  };

  const handleUnlink = () => {
    persistLinkedCode("");
    setBoard(null);
    setErrorMessage("");
  };

  const pageStyle = useMemo(
    () => ({
      ...styles.page,
      background:
        branch?.primaryColor
          ? `linear-gradient(180deg, ${branch.primaryColor}12 0%, #f8fafc 32%, #eef2ff 100%)`
          : styles.page.background,
    }),
    [branch?.primaryColor]
  );

  if (!linkedCode) {
    return (
      <div style={styles.setupPage}>
        <div style={styles.setupCard}>
          <div style={styles.eyebrow}>MozzoApp Display</div>
          <h1 style={styles.setupTitle}>Vincular pantalla</h1>
          <p style={styles.setupText}>
            Escribe el codigo que ves en la sucursal para mostrar solo los pedidos de
            preparacion y listo para retirar de ese local.
          </p>

          <form onSubmit={handleSubmitCode} style={styles.setupForm}>
            <input
              type="text"
              value={codeInput}
              onChange={(event) => setCodeInput(normalizePairingCodeInput(event.target.value))}
              placeholder="Ej: MZ-ABCD-EF12-3456"
              style={styles.setupInput}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck="false"
            />
            <button type="submit" style={styles.primaryButton}>
              Abrir pantalla
            </button>
          </form>

          {errorMessage ? <div style={styles.errorBanner}>{errorMessage}</div> : null}
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={styles.shell}>
        <div style={styles.header}>
          <div style={styles.headerBrand}>
            {branchIconUrl ? (
              <img
                src={branchIconUrl}
                alt={branch?.name || "Sucursal"}
                style={styles.headerLogo}
              />
            ) : null}
            <div style={styles.headerCopy}>
              <div style={styles.eyebrow}>MozzoApp Display</div>
              <h1 style={styles.headerTitle}>{branch?.name || "Pantalla publica"}</h1>
              <div style={styles.headerMeta}>
                Codigo {linkedCode} · Actualizado {formatBoardTimestamp(board?.updatedAt)}
              </div>
            </div>
          </div>

          <button type="button" onClick={handleUnlink} style={styles.secondaryButton}>
            Cambiar codigo
          </button>
        </div>

        {errorMessage ? <div style={styles.errorBanner}>{errorMessage}</div> : null}

        <div style={styles.boardGrid}>
          <section style={styles.boardColumn}>
            <div style={styles.columnHeader}>
              <div>
                <div style={styles.columnEyebrow}>En curso</div>
                <h2 style={styles.columnTitle}>Preparacion</h2>
              </div>
              <div style={styles.columnCount}>{preparingOrders.length}</div>
            </div>

            <div style={styles.cardsGrid}>
              {preparingOrders.length > 0 ? (
                preparingOrders.map((order) => (
                  <article key={`preparing-${order.id}-${order.preparationNumber}`} style={styles.orderCard}>
                    <div style={styles.orderNumber}>
                      {formatPreparationNumber(order.preparationNumber)}
                    </div>
                    <div style={styles.orderName}>{order.displayName || "Pedido"}</div>
                  </article>
                ))
              ) : (
                <div style={styles.emptyCard}>
                  {loading ? "Cargando pedidos..." : "No hay pedidos en preparacion."}
                </div>
              )}
            </div>
          </section>

          <section style={styles.boardColumn}>
            <div style={styles.columnHeader}>
              <div>
                <div style={styles.columnEyebrow}>Entrega</div>
                <h2 style={styles.columnTitle}>Listo para retirar</h2>
              </div>
              <div style={styles.columnCount}>{readyOrders.length}</div>
            </div>

            <div style={styles.cardsGrid}>
              {readyOrders.length > 0 ? (
                readyOrders.map((order) => (
                  <article
                    key={`ready-${order.id}-${order.preparationNumber}`}
                    style={styles.readyOrderCard}
                  >
                    <div style={styles.readyOrderNumber}>
                      {formatPreparationNumber(order.preparationNumber)}
                    </div>
                    <div style={styles.readyOrderName}>{order.displayName || "Pedido"}</div>
                  </article>
                ))
              ) : (
                <div style={styles.emptyCard}>
                  {loading ? "Cargando pedidos..." : "Todavia no hay pedidos listos."}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default DisplayPedidos;

const styles = {
  page: {
    minHeight: "100vh",
    padding: "24px",
    background: "linear-gradient(180deg, #fff7ed 0%, #f8fafc 32%, #eef2ff 100%)",
  },
  setupPage: {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    padding: "24px",
    background: "linear-gradient(180deg, #fff7ed 0%, #f8fafc 100%)",
  },
  shell: {
    width: "100%",
    maxWidth: "1480px",
    margin: "0 auto",
    display: "grid",
    gap: "20px",
  },
  setupCard: {
    width: "100%",
    maxWidth: "620px",
    display: "grid",
    gap: "18px",
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "24px",
    padding: "28px",
    boxShadow: "0 24px 70px rgba(15, 23, 42, 0.08)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    padding: "18px 22px",
    borderRadius: "24px",
    border: "1px solid #dbe2ea",
    backgroundColor: "rgba(255,255,255,0.94)",
    boxShadow: "0 18px 50px rgba(15, 23, 42, 0.05)",
  },
  headerBrand: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    minWidth: 0,
  },
  headerLogo: {
    width: "72px",
    height: "72px",
    objectFit: "contain",
    borderRadius: "16px",
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
    padding: "10px",
    flexShrink: 0,
  },
  headerCopy: {
    display: "grid",
    gap: "4px",
    minWidth: 0,
  },
  eyebrow: {
    fontSize: "13px",
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#f97316",
  },
  headerTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: "40px",
    lineHeight: 1,
  },
  headerMeta: {
    color: "#64748b",
    fontSize: "16px",
    lineHeight: 1.5,
  },
  setupTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: "36px",
    lineHeight: 1.05,
  },
  setupText: {
    margin: 0,
    color: "#475569",
    fontSize: "16px",
    lineHeight: 1.6,
  },
  setupForm: {
    display: "grid",
    gap: "12px",
  },
  setupInput: {
    width: "100%",
    borderRadius: "16px",
    border: "1px solid #cbd5e1",
    padding: "18px 20px",
    fontSize: "24px",
    fontWeight: 700,
    color: "#0f172a",
    boxSizing: "border-box",
    textTransform: "uppercase",
  },
  boardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "20px",
  },
  boardColumn: {
    display: "grid",
    gap: "16px",
    padding: "20px",
    borderRadius: "24px",
    border: "1px solid #dbe2ea",
    backgroundColor: "rgba(255,255,255,0.94)",
    boxShadow: "0 18px 50px rgba(15, 23, 42, 0.05)",
    minHeight: "70vh",
    alignContent: "start",
  },
  columnHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
  },
  columnEyebrow: {
    color: "#64748b",
    fontSize: "14px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  columnTitle: {
    margin: "4px 0 0",
    color: "#0f172a",
    fontSize: "32px",
    lineHeight: 1.05,
  },
  columnCount: {
    minWidth: "56px",
    height: "56px",
    borderRadius: "999px",
    border: "1px solid #cbd5e1",
    display: "grid",
    placeItems: "center",
    fontSize: "24px",
    fontWeight: 800,
    color: "#0f172a",
    backgroundColor: "#ffffff",
    flexShrink: 0,
  },
  cardsGrid: {
    display: "grid",
    gap: "14px",
    alignContent: "start",
  },
  orderCard: {
    display: "grid",
    gap: "10px",
    borderRadius: "22px",
    border: "1px solid #bfdbfe",
    backgroundColor: "#eff6ff",
    padding: "22px",
  },
  readyOrderCard: {
    display: "grid",
    gap: "10px",
    borderRadius: "22px",
    border: "1px solid #bbf7d0",
    backgroundColor: "#ecfdf5",
    padding: "22px",
  },
  orderNumber: {
    color: "#1d4ed8",
    fontSize: "42px",
    fontWeight: 900,
    lineHeight: 1,
  },
  readyOrderNumber: {
    color: "#166534",
    fontSize: "42px",
    fontWeight: 900,
    lineHeight: 1,
  },
  orderName: {
    color: "#0f172a",
    fontSize: "26px",
    fontWeight: 800,
    lineHeight: 1.15,
    textTransform: "uppercase",
    wordBreak: "break-word",
  },
  readyOrderName: {
    color: "#14532d",
    fontSize: "26px",
    fontWeight: 800,
    lineHeight: 1.15,
    textTransform: "uppercase",
    wordBreak: "break-word",
  },
  emptyCard: {
    minHeight: "180px",
    borderRadius: "20px",
    border: "1px dashed #cbd5e1",
    backgroundColor: "#f8fafc",
    color: "#64748b",
    display: "grid",
    placeItems: "center",
    padding: "18px",
    textAlign: "center",
    fontSize: "20px",
    lineHeight: 1.5,
  },
  errorBanner: {
    borderRadius: "16px",
    border: "1px solid #fecaca",
    backgroundColor: "#fff1f2",
    color: "#b91c1c",
    padding: "14px 16px",
    fontSize: "15px",
    lineHeight: 1.5,
  },
  primaryButton: {
    border: "none",
    borderRadius: "16px",
    backgroundColor: "#2563eb",
    color: "#ffffff",
    fontSize: "18px",
    fontWeight: 800,
    padding: "16px 18px",
    cursor: "pointer",
  },
  secondaryButton: {
    border: "1px solid #cbd5e1",
    borderRadius: "14px",
    backgroundColor: "#ffffff",
    color: "#334155",
    fontSize: "15px",
    fontWeight: 700,
    padding: "12px 16px",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
};
